// Hjem page - Landing / welcome page for Kulinarisk Laug
// Shows a hero section with stats and hardcoded upcoming events

import React, { useState, useEffect } from 'react';
import { getAuth, onAuthStateChanged, signInWithPopup, GoogleAuthProvider } from 'firebase/auth';
import { app } from './firebase-config';
import { LiveAchievements, LiveScoreboard, SpesielleUtmerkelser } from './Utmerkelser';
import { TbInfinity } from 'react-icons/tb';
import { Link } from 'react-router-dom';
import { FiMapPin, FiUser, FiArrowRight } from 'react-icons/fi';
import FerskeMinner from './FerskeMinner';
import { getFirestore, collection, getDocs } from 'firebase/firestore';
import { mergeUpcoming, quarterOf } from './upcoming';

// Event type from the event name, e.g. "🍷Vino" -> icon, title and colour token
const EVENT_TYPES = [
  { match: /vino/i, label: 'Vino', color: 'var(--color-vino)' },
  { match: /blåtur/i, label: 'Blåtur', color: 'var(--color-blatur)' },
  { match: /julebord/i, label: 'Julebord', color: 'var(--color-julebord)' },
  { match: /julegrøt/i, label: 'Julegrøt', color: 'var(--color-julegrot)' },
  { match: /fitness/i, label: 'Fitness', color: 'var(--color-fitness)' },
  { match: /\bKD\b|kunnskap/i, label: 'KD', color: 'var(--color-kd)' },
  { match: /kulinarisk|\bKL\b/i, label: 'KL', color: 'var(--color-kl)' },
];
const splitEmoji = (name) => {
  const m = name.match(/\p{Extended_Pictographic}\uFE0F?/u);
  if (!m) return { icon: '✦', title: name };
  return { icon: m[0], title: name.replace(m[0], '').trim() };
};

// Old hardcoded event -> the same card shape as database events (see upcoming.js)
const legacyCard = (evt, i) => {
  const { icon, title } = splitEmoji(evt.name);
  const type = EVENT_TYPES.find(t => t.match.test(evt.name));
  const [q, year] = (evt.quarter || '').split(' ');
  return {
    key: `legacy-${i}`,
    icon,
    typeLabel: type?.label || title,
    color: type?.color || 'var(--accent)',
    title,
    date: evt.date || null,
    quarter: evt.date ? quarterOf(evt.date) : `${year} ${q}`,
    desc: evt.desc,
    location: evt.location,
    hosts: evt.host,
    tags: evt.tags || [],
  };
};

const MARQUEE = ['🥩 Kulinarisk Laug', '🍷 Vino', '🌍 Blåtur', '🧠 KD', '🎅 Julegrøt', '🎄 Julebord', '💪 Fitness'];

// Helper: format a Date as "8. MARS 2026" (Norwegian uppercase month)
const formatDate = (date) => {
  const months = ['JANUAR','FEBRUAR','MARS','APRIL','MAI','JUNI','JULI','AUGUST','SEPTEMBER','OKTOBER','NOVEMBER','DESEMBER'];
  return `${date.getDate()}. ${months[date.getMonth()]} ${date.getFullYear()}`;
};

// Helper: days from today until a given date
const daysUntil = (date) => {
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const target = new Date(date);
  target.setHours(0, 0, 0, 0);
  return Math.ceil((target - now) / (1000 * 60 * 60 * 24));
};

// Quarter helpers
const quarterLabels = { Q1: 'JAN–MAR', Q2: 'APR–JUN', Q3: 'JUL–SEP', Q4: 'OKT–DES' };

const formatQuarter = (kvartal) => {
  // kvartal is e.g. "2026 Q2"
  const [year, qPart] = kvartal.split(' ');
  return `${qPart} (${quarterLabels[qPart]}) ${year}`;
};

const quarterCountdown = () => 'Dato ikke satt';

// ── Old hardcoded upcoming events ──
// Upcoming events now come from the database (Admin → Eventer, with a date or a future quarter).
// These are only shown until an event of the same type and quarter exists there, and can be deleted then.
const upcomingEvents = [
        {
    //date: new Date(2026, 10, 28),   // 12. mars 2026  (month is 0-indexed)
    quarter: 'Q1 2027', // Alternative to date - use when only quarter is known
    name: '🥩Kulinarisk Laug',
    desc: 'Anders fikk tildelt ansvaret av Gil forrige KL. Han har allerede arrangert Vino i 2025 på mesterliv vis, og vil uten tvil gjøre en strålende jobb som vert for KL. Stay tuned!',
    location: '???',
    host: 'Anders L',
    tags: ['KL rookie'],
  },
      {
    date: new Date(2026, 10, 28),   // 12. mars 2026  (month is 0-indexed)
    quarter: 'Q4 2026', // Alternative to date - use when only quarter is known
    name: 'Julebord🎄',
    desc: 'Nikolai har allerede arrangert en rekke vellykkede julebord tidligere. Således har han inntatt en ledende rolle for videreføringen av julebord som tradisjon. Respekt!🎅 Det blir som vanlig vorspiel før hovedarrangementet et møysommelig utvalgt sted. Det er bare å glede seg!',
    location: '???',
    host: 'Nikolai',
    tags: ['Julebord Specialist'],
  },
    {
    date: new Date(2026, 9, 21),   // 12. mars 2026  (month is 0-indexed)
    quarter: 'Q4 2026', // Alternative to date - use when only quarter is known
    name: '🍷Vino',
    desc: 'Gustaf er allerede ⚜️Festivity Specialist⚜️ fordi han har arrangert Vino🍷 to ganger tidligere. Denne gangen er temaet "Txakoli og Pintxos" - altså baskisk aften! Kanskje lett og frisk hvitvin med små barretter? Meget spennende og garantert suksess med Gustaf som vert! ',
    location: 'Nationaltheatret Konferansesenter, 17:00',
    host: 'Gustaf',
    tags: ['Vino Specialist', 'Vino Expert'],
  },
  {
    date: new Date(2026, 9, 2),   // 12. mars 2026  (month is 0-indexed)
    quarter: 'Q4 2026', // Alternative to date - use when only quarter is known
    name: '🥩Kulinarisk Laug',
    desc: 'Kjære deltakere, dere er hjertelige velkommen til Rådhusgata 17, kl 17.00, dere kan ringe på Gture. Der etter blir det en kort velkoms hilsen før vi beveger oss til en mer lugubre lokasjon for en liten konkurranse før man beveger seg til et kapital rik område for å få en klassisk og historisk opplevelse!',
    location: 'Rådhusgata 17, kl 17:00',
    host: 'Gil',
    tags: ['KL'],
  },
      {
    date: new Date(2026, 8, 11),  // 22. mars 2026
    name: '🌍Blåtur',
    desc: 'Med budsjett på 10.000 og engasjment i fyr og flamme ble dette en legendarisk opplevelse. Konseptet for Blåtur 2026 med 8 deltakere var Toscana Roadtrip Games🍷☀️Vi kjørte 400km fra Pisa til Roma med lagkonkurranse hele veien🔥🚗 Neste års arrangører er Njaal og Anders S!👑',
    location: "Italia-Toscana",
    host: 'Thomas & Matias',
    tags: ['Epic', 'Travel life'],
  },
  {
    date: new Date(2026, 3, 25),  // 22. mars 2026
    quarter: 'Q2 2026', // Alternative to date - use when only quarter is known
    name: '🥩Kulinarisk Laug',
    desc: 'Martin er selve grunnleggeren av Det Kulinariske Laug. Sist han arrangerte 🥩KL var i 2017 på "Festningen" som lenge toppet KL-rankingen. Når Martin står bak arrangementet, kan vi forvente en kveld fylt med kreativitet, smakfull mat og en atmosfære som oser av gastronomisk glede. Kina-KL Q12026 gikk av stabelen på Yum Cha, som var et fortreffelig valg av Martin🔥👌 God stemning; bla. fortalte Bjering om ny jobb, Martin snakket om romersk krigføring og Thomas flørtet med damebordet ved siden av😂 I tradisjon tro delte Glenn ut KL-rating som falt på 3,65 denne gangen og Martin bestemte neste KL-arrangør, som ble Gil🎉',
    location: "Yum Cha",
    host: 'Martin',
    tags: ['Asiatisk', 'The Godfather of KL'],
  }
];

const Hjem = () => {
  const eventCount = 60;    // Hardcoded
  const memberCount = 27;   // Hardcoded
  // awardsCount replaced by infinity icon below
  const [activeTab, setActiveTab] = useState('kommende');
  const [user, setUser] = useState(null);
  const [dbEvents, setDbEvents] = useState([]);

  // Upcoming events from the database (readable when logged in)
  useEffect(() => {
    if (!user) return;
    getDocs(collection(getFirestore(app), 'events'))
      .then(snap => setDbEvents(snap.docs.map(d => ({ id: d.id, ...d.data() }))))
      .catch(error => console.error('Error fetching upcoming events: ', error));
  }, [user]);
  const upcoming = mergeUpcoming(dbEvents, upcomingEvents.map(legacyCard));

  useEffect(() => {
    const auth = getAuth(app);
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
    });
    return () => unsubscribe();
  }, []);

  const handleLogin = async () => {
    const auth = getAuth(app);
    const provider = new GoogleAuthProvider();
    try {
      await signInWithPopup(auth, provider);
    } catch (error) {
      console.error('Error with Google sign-in:', error);
    }
  };

  return (
    <div className="hjem-page">
      {/* Hero section */}
      <section className="hjem-hero">
        <div className="hjem-hero-copy">
          <span className="hjem-eyebrow"><span className="hjem-pulse" />Sesong 2026 · siden 2017</span>
          <h1 className="hjem-title">
            Kulinarisk <span className="text-gradient">Laug</span>
          </h1>
          <p className="hjem-lead">
            Det handler ikke om maten. Det handler om hvem som sitter rundt bordet.
          </p>
          <p className="hjem-subtitle">
            Siden 2017 har vi samlet gjengen til middager, vinkvelder, foredrag og turer med ukjent destinasjon.
            Anledningene varierer, men de gode samtalene og vennskapene består. Og ja, vi fører poeng.
          </p>
          <div className="hjem-cta">
            <Link to="/forside" className="button-17">Alle eventer <FiArrowRight /></Link>
            <Link to="/blatur" className="button-17 muted">🌍 Blåtur</Link>
          </div>
        </div>

        <div className="hjem-hero-art" aria-hidden="true">
          <div className="hjem-orb" />
          <svg className="hjem-badge-ring" viewBox="0 0 200 200">
            <defs>
              <path id="ring" d="M100,100 m-78,0 a78,78 0 1,1 156,0 a78,78 0 1,1 -156,0" />
            </defs>
            <text>
              <textPath href="#ring" textLength="486" lengthAdjust="spacing">GODE SAMTALER ✦ GODE VENNER ✦ SIDEN 2017 ✦</textPath>
            </text>
          </svg>
          <span className="hjem-badge-center">🍽️</span>
          <span className="hjem-float f1">🍷</span>
          <span className="hjem-float f2">🌍</span>
          <span className="hjem-float f3">🥩</span>
          <span className="hjem-float f4">🧠</span>
        </div>
      </section>

      {/* Stats */}
      <div className="hjem-stats">
        <div className="hjem-stat">
          <span className="hjem-stat-number">{eventCount}</span>
          <span className="hjem-stat-label">Tidligere eventer</span>
        </div>
        <div className="hjem-stat">
          <span className="hjem-stat-number">{memberCount}</span>
          <span className="hjem-stat-label">Medlemmer</span>
        </div>
        <div className="hjem-stat">
          <span className="hjem-stat-number"><TbInfinity /></span>
          <span className="hjem-stat-label">Minner</span>
        </div>
      </div>

      {/* Event types running past */}
      <div className="hjem-marquee" aria-hidden="true">
        <div className="hjem-marquee-track">
          {[...MARQUEE, ...MARQUEE].map((item, i) => <span key={i}>{item}<b>✦</b></span>)}
        </div>
      </div>

      {/* Tab badges */}
      <div className="hjem-tab-bar">
        <button
          className={`hjem-tab${activeTab === 'kommende' ? ' active' : ''}`}
          onClick={() => setActiveTab('kommende')}
        >
          Kommende eventer
        </button>
        <button
          className={`hjem-tab${activeTab === 'vandrepokaler' ? ' active' : ''}`}
          onClick={() => setActiveTab('vandrepokaler')}
        >
          Vandrepokaler
        </button>
        <button
          className={`hjem-tab${activeTab === 'live' ? ' active' : ''}`}
          onClick={() => setActiveTab('live')}
        >
          Utmerkelser
        </button>
        <button
          className={`hjem-tab${activeTab === 'spesielle' ? ' active' : ''}`}
          onClick={() => setActiveTab('spesielle')}
        >
          Premieringer
        </button>
      </div>

      {/* Tab content */}
      {activeTab === 'kommende' && (
      <section className="hjem-events">
        <div className="hjem-events-grid">
          {upcoming.length === 0 && (
            <p className="hjem-login-gate">Ingen kommende eventer er lagt inn ennå.</p>
          )}
          {upcoming.map((c) => {
            const dateText = c.date ? formatDate(c.date) : formatQuarter(c.quarter);
            const countdownText = c.date
              ? (() => { const d = daysUntil(c.date); return d > 0 ? `Om ${d} dager` : 'I dag!'; })()
              : quarterCountdown();
            return (
              <article className="hjem-ticket" key={c.key} style={{ '--cat': c.color }}>
                <div className="hjem-ticket-stub">
                  <span className="hjem-ticket-icon">{c.icon}</span>
                  <span className="hjem-ticket-countdown">{countdownText}</span>
                </div>
                <div className="hjem-ticket-body">
                  <span className="hjem-ticket-date">{dateText}</span>
                  <h3 className="hjem-ticket-title">{c.title}</h3>
                  {c.desc && <p className="hjem-ticket-desc">{c.desc}</p>}
                  <dl className="hjem-ticket-meta">
                    {c.location && <div><dt><FiMapPin /></dt><dd>{c.location}</dd></div>}
                    {c.hosts && <div><dt><FiUser /></dt><dd>Vert: {c.hosts}</dd></div>}
                  </dl>
                  {c.tags.length > 0 && (
                    <div className="hjem-event-tags">
                      {c.tags.map((tag, j) => (
                        <span className="hjem-tag" key={j}>{tag}</span>
                      ))}
                    </div>
                  )}
                </div>
              </article>
            );
          })}
          {!user && upcoming.length > 0 && (
            <p className="hjem-login-gate">Logg inn for å se alle kommende eventer.</p>
          )}
        </div>
      </section>
      )}
      {activeTab === 'vandrepokaler' && (user ? <LiveScoreboard /> : (
        <div className="hjem-login-gate">
          <p>Logg inn for å se vandrepokalene</p>
          <button className="button-17" onClick={handleLogin}>Logg inn</button>
        </div>
      ))}
      {activeTab === 'live' && (user ? <LiveAchievements /> : (
        <div className="hjem-login-gate">
          <p>Logg inn for å se utmerkelsene</p>
          <button className="button-17" onClick={handleLogin}>Logg inn</button>
        </div>
      ))}
      {activeTab === 'spesielle' && <SpesielleUtmerkelser />}

      {/* Photos from recent events, below the upcoming events */}
      <FerskeMinner />
    </div>
  );
};

export default Hjem;
